"use client"

import { useField } from "formik"
import type { ComponentProps } from "react"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"

type FormTextareaProps = Omit<ComponentProps<"textarea">, "name"> & {
  name: string
  label: string
  description?: string
  showLength?: boolean
}

export function FormTextarea({
  name,
  label,
  description,
  showLength = false,
  id = name,
  ...props
}: FormTextareaProps) {
  const [field, meta] = useField<string>(name)
  const error = meta.touched && meta.error ? meta.error : null

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {showLength && typeof props.maxLength === "number" ? (
          <span className="text-xs text-muted-foreground">{field.value.length}/{props.maxLength}</span>
        ) : null}
      </div>
      <Textarea
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
        {...field}
        {...props}
      />
      {description ? (
        <p id={`${id}-description`} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
